import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Building2, Plus, Check, ArrowRight, Crown, UserCircle2 } from "lucide-react";
import EmptyState from "@/components/EmptyState";

/**
 * WorkspacesPage — full management screen for every workspace the user
 * belongs to: switch, create, and rename (if owner).
 */
export default function WorkspacesPage() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const load = () => {
    api.get("/workspaces/mine").then(({ data }) => setRows(data?.workspaces || [])).catch(() => setRows([]));
  };

  useEffect(load, []);

  const activeId = user?.active_workspace_id || user?.id;

  const doSwitch = async (wsId) => {
    if (wsId === activeId) return;
    try {
      await api.post("/workspaces/switch", { workspace_id: wsId });
      await refreshMe();
      toast.success("Switched workspace");
      window.location.reload();
    } catch {
      toast.error("Failed to switch");
    }
  };

  const doCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Give it a name"); return; }
    try {
      await api.post("/workspaces", { name: trimmed });
      await refreshMe();
      toast.success(`Created "${trimmed}"`);
      setName("");
      setCreating(false);
      window.location.reload();
    } catch {
      toast.error("Failed to create workspace");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="workspaces-page">
      <div className="mb-8">
        <div className="section-label">Multi-business</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl md:text-5xl mt-2">
          WORKSPACES
        </h1>
        <p className="text-sm text-[var(--peak-muted)] mt-3 max-w-2xl">
          Every business you own or work in. Switch between them, rename yours, or add a new one — each keeps its own contacts, pipelines, calendar and billing.
        </p>
      </div>

      {/* Create row */}
      <div className="brutalist-card p-5 mb-6 flex items-center gap-4">
        {creating ? (
          <>
            <Plus className="w-5 h-5 text-[var(--peak-accent)] flex-none" />
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doCreate(); if (e.key === "Escape") { setCreating(false); setName(""); } }}
              placeholder="New business name (e.g. Acme Studio)"
              data-testid="ws-page-name-input"
              className="brutalist-input flex-1"
            />
            <button
              type="button"
              onClick={doCreate}
              data-testid="ws-page-save"
              className="px-4 py-2 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] font-heading font-bold uppercase text-xs tracking-wider"
            >
              Create workspace
            </button>
            <button
              type="button"
              onClick={() => { setCreating(false); setName(""); }}
              className="px-3 py-2 text-[var(--peak-muted)] font-mono uppercase text-xs tracking-wider"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="ws-page-create-btn"
            className="w-full flex items-center gap-3 text-left"
          >
            <div className="w-10 h-10 border-2 border-dashed border-[var(--peak-ink)] flex items-center justify-center flex-none">
              <Plus className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-black text-lg tracking-tighter">Start a new business</div>
              <div className="text-xs text-[var(--peak-muted)] font-mono uppercase tracking-widest mt-1">
                Fresh workspace · 14-day trial included
              </div>
            </div>
          </button>
        )}
      </div>

      {/* List */}
      {rows === null && (
        <div className="text-sm text-[var(--peak-muted)]">Loading…</div>
      )}
      {rows && rows.length === 0 && (
        <EmptyState
          icon={Building2}
          title="No workspaces yet"
          hint="Create your first business above"
          testId="workspaces-empty"
        />
      )}
      {rows && rows.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="workspaces-grid">
          {rows.map((w) => {
            const active = w.workspace_id === activeId;
            return (
              <div
                key={w.workspace_id}
                className={`brutalist-card p-5 flex flex-col gap-3 relative ${active ? "peak-glow" : ""}`}
                data-testid={`ws-card-${w.workspace_id}`}
              >
                <div className="flex items-start gap-3">
                  {w.logo ? (
                    <img src={w.logo} alt="" className="w-10 h-10 object-cover border-2 border-[var(--peak-ink)] flex-none" />
                  ) : (
                    <div className="w-10 h-10 bg-[var(--peak-accent)] flex items-center justify-center flex-none">
                      <span className="text-[var(--peak-accent-contrast)] font-black text-lg font-heading">
                        {(w.name || "?").charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-black tracking-tighter text-lg leading-tight truncate">
                      {w.name}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-1 flex items-center gap-1">
                      {w.role === "owner" ? <Crown className="w-3 h-3" /> : <UserCircle2 className="w-3 h-3" />}
                      {w.role}
                    </div>
                  </div>
                  {active && (
                    <div className="flex-none w-6 h-6 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] flex items-center justify-center">
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    </div>
                  )}
                </div>

                <div className="text-xs text-[var(--peak-muted)] font-mono">
                  {w.plan?.toUpperCase()} · Since {(w.created_at || "").slice(0, 10)}
                </div>

                {!active ? (
                  <button
                    type="button"
                    onClick={() => doSwitch(w.workspace_id)}
                    data-testid={`ws-switch-${w.workspace_id}`}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wider hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)] transition-colors mt-2"
                  >
                    Switch to this workspace
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate("/settings")}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] font-heading font-bold uppercase text-xs tracking-wider mt-2"
                  >
                    Currently active · Open settings
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
